// ============================================
// Đơn vị tiền tệ DÙNG CHUNG toàn store-admin.
//
// Bug #129 (Promotions hiện "$" ở nút chọn nhưng "VND" ở ô nhập) và bug #160
// (Inventory hiện "PLN" trong khi phần còn lại của app hiện "$") đều bắt nguồn
// từ việc mỗi màn hình tự format tiền theo cách riêng. Mọi chỗ hiển thị tiền
// PHẢI đi qua file này — sửa một chỗ, đổi toàn app.
//
// ĐA TIỀN TỆ (sheet 2 mục #75, 03/09/2026)
// ----------------------------------------
// Trước đây file này hard-code UAH cho toàn bộ app. Với chuỗi nhiều thị trường
// (PL dùng PLN, UA dùng UAH) thì mọi con số ở màn quản trị đều dán sai ký hiệu
// — và sai kiểu im lặng: số vẫn đúng, chỉ ký hiệu là bịa.
//
// `Store.currency` đã có sẵn ở BE, nên cách đúng là TRUYỀN currency xuống từng
// chỗ hiển thị. Chữ ký cũ `formatMoney(value)` vẫn chạy để không phải sửa một
// lượt cả trăm chỗ gọi; chỗ nào biết currency thì truyền, chỗ chưa truyền sẽ
// rơi về mặc định như cũ.
//
// ⚠️ KHÔNG BAO GIỜ cộng hai số khác currency. Tổng chuỗi nhiều thị trường phải
// tách theo từng loại tiền — xem `groupByCurrency` / `hasMixedCurrencies`.
// ============================================

/**
 * Tiền tệ mặc định khi KHÔNG biết cửa hàng nào.
 *
 * Đây là fallback, không phải "tiền tệ của app". Chỗ nào có `Store.currency`
 * hoặc `currency` trên chính bản ghi thì phải truyền vào — dùng mặc định ở màn
 * có dữ liệu nhiều cửa hàng là dán sai ký hiệu lên số của thị trường khác.
 */
export const APP_CURRENCY = 'UAH';

/** Ký hiệu của tiền tệ mặc định. Giữ tên cũ vì nhiều nơi đang import. */
export const APP_CURRENCY_SYMBOL = '₴';

/** Locale dùng để nhóm chữ số. Giữ 'en-US' để khớp layout hiện có (1,234.56). */
const GROUPING_LOCALE = 'en-US';

type CurrencyConfig = {
  symbol: string;
  /** Ký hiệu đứng trước hay sau số. */
  position: 'prefix' | 'suffix';
  /** Có khoảng trắng giữa ký hiệu và số không. */
  space: boolean;
};

/**
 * Bảng ký hiệu cho các thị trường đang chạy.
 *
 * Mã lạ KHÔNG rơi về ký hiệu mặc định: hiện chính mã ISO (`"SEK 1,234.56"`).
 * Thà đọc hơi thô còn hơn hiện ký hiệu của một loại tiền khác — đó đúng là
 * bug #129/#160 ở quy mô quốc gia.
 */
const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  UAH: { symbol: '₴', position: 'prefix', space: false },
  PLN: { symbol: 'zł', position: 'suffix', space: true },
  USD: { symbol: '$', position: 'prefix', space: false },
  EUR: { symbol: '€', position: 'prefix', space: false },
  VND: { symbol: '₫', position: 'suffix', space: true },
};

/**
 * Mã tiền tệ có ký hiệu riêng trong hệ thống — dùng để dựng droplist chọn tiền
 * tệ cho cửa hàng.
 *
 * Suy THẲNG từ `CURRENCY_CONFIG` chứ không khai một mảng song song: hai danh
 * sách rời nhau thì sớm muộn cũng lệch, và lệch ở đây nghĩa là chọn được một
 * mã mà `formatMoney` không biết ký hiệu, rồi cả hệ thống in ra mã ISO thô.
 *
 * Mã ngoài danh sách vẫn HỢP LỆ với BE (`Store.currency` là chuỗi tự do) —
 * chỉ là chưa có ký hiệu đẹp. Nơi hiển thị phải chịu được ca đó, xem `configFor`.
 */
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_CONFIG);

function configFor(currency?: string | null): CurrencyConfig {
  const code = (currency || APP_CURRENCY).trim().toUpperCase();
  return (
    CURRENCY_CONFIG[code] ?? {
      symbol: code,
      position: 'prefix',
      space: true,
    }
  );
}

/** Ký hiệu đứng một mình, dùng cho nhãn ngắn / placeholder ô nhập. */
export function currencySymbol(currency?: string | null): string {
  return configFor(currency).symbol;
}

function withSymbol(body: string, currency?: string | null): string {
  const config = configFor(currency);
  const gap = config.space ? ' ' : '';
  return config.position === 'prefix'
    ? `${config.symbol}${gap}${body}`
    : `${body}${gap}${config.symbol}`;
}

interface FormatMoneyOptions {
  /** Số chữ số thập phân tối thiểu (mặc định 2). */
  minimumFractionDigits?: number;
  /** Số chữ số thập phân tối đa (mặc định 2). */
  maximumFractionDigits?: number;
  /**
   * Mã ISO 4217 của bản ghi đang hiển thị (`Store.currency`,
   * `ReturnRequest.currency`...). Bỏ trống = dùng mặc định của app.
   */
  currency?: string | null;
}

/**
 * Format tiền đầy đủ: `₴1,234.56`, `1 234,56 zł`.
 * Dùng cho bảng, thẻ số liệu, modal chi tiết — mọi nơi cần con số chính xác.
 */
export function formatMoney(value: number, options?: FormatMoneyOptions): string {
  if (!Number.isFinite(value)) return withSymbol('0.00', options?.currency);

  const formatted = value.toLocaleString(GROUPING_LOCALE, {
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  });

  return withSymbol(formatted, options?.currency);
}

/**
 * Format rút gọn cho trục biểu đồ / thẻ nhỏ: `₴1.2k`, `₴3.4M`.
 * Dưới 1000 thì trả về số nguyên có ký hiệu: `₴940`.
 */
export function formatMoneyCompact(
  value: number,
  fractionDigits = 1,
  currency?: string | null
): string {
  if (!Number.isFinite(value)) return withSymbol('0', currency);

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000) {
    return `${sign}${withSymbol(`${(abs / 1_000_000).toFixed(fractionDigits)}M`, currency)}`;
  }
  if (abs >= 1_000) {
    return `${sign}${withSymbol(`${(abs / 1_000).toFixed(fractionDigits)}k`, currency)}`;
  }
  return `${sign}${withSymbol(Math.round(abs).toLocaleString(GROUPING_LOCALE), currency)}`;
}

/**
 * Format có dấu +/- ở đầu — dùng cho cột lợi nhuận/chênh lệch.
 */
export function formatMoneySigned(value: number, options?: FormatMoneyOptions): string {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}${formatMoney(Math.abs(value), options)}`;
}

// ============================================
// Chống cộng nhầm giữa các loại tiền
// ============================================

/**
 * Các mã tiền tệ KHÁC NHAU xuất hiện trong một tập bản ghi.
 * Bỏ qua bản ghi không khai currency (coi như thuộc mặc định).
 */
export function distinctCurrencies(
  items: Array<{ currency?: string | null }> | undefined | null
): string[] {
  if (!items || items.length === 0) return [];
  const set = new Set<string>();
  for (const item of items) {
    const code = item.currency?.trim().toUpperCase();
    if (code) set.add(code);
  }
  return [...set];
}

/**
 * `true` khi tập dữ liệu trộn từ hai loại tiền trở lên — lúc đó MỌI phép cộng
 * gộp (tổng doanh thu, giá trị tồn kho, tổng hoàn tiền...) đều vô nghĩa và
 * màn hình phải nói ra điều đó thay vì hiện một con số trông có vẻ đúng.
 */
export function hasMixedCurrencies(
  items: Array<{ currency?: string | null }> | undefined | null
): boolean {
  return distinctCurrencies(items).length > 1;
}

/** Gộp tổng TÁCH RIÊNG theo từng loại tiền: `{ PLN: 89, UAH: 1200 }`. */
export function groupByCurrency<T extends { currency?: string | null }>(
  items: T[],
  amountOf: (item: T) => number
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const item of items) {
    const code = (item.currency?.trim().toUpperCase() || APP_CURRENCY);
    const amount = amountOf(item);
    if (!Number.isFinite(amount)) continue;
    totals[code] = (totals[code] ?? 0) + amount;
  }
  return totals;
}
